import React from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { Shield, Lock, TrendingUp, FileSignature, Users, Landmark } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { t } from "@/i18n";

export default function ComeFunziona() {
  const { user, lang } = useApp();
  const navigate = useNavigate();

  const steps = [
    { num: "01", icon: FileSignature, title: t("cf_step1_t", lang), desc: t("cf_step1_d", lang) },
    { num: "02", icon: Lock,          title: t("cf_step2_t", lang), desc: t("cf_step2_d", lang) },
    { num: "03", icon: TrendingUp,    title: t("cf_step3_t", lang), desc: t("cf_step3_d", lang) },
  ];
  const guarantees = [
    { icon: Shield,   title: t("cf_g1_t", lang), desc: t("cf_g1_d", lang) },
    { icon: Users,    title: t("cf_g2_t", lang), desc: t("cf_g2_d", lang) },
    { icon: Landmark, title: t("cf_g3_t", lang), desc: t("cf_g3_d", lang) },
  ];

  return (
    <PageShell
      testid="come-funziona-page"
      eyebrow={t("cf_eyebrow", lang)}
      title={t("cf_title", lang)}
      intro={t("cf_intro", lang)}
    >
      <div className="grid gap-px bg-[#D8CFBB] md:grid-cols-3 mb-16" data-testid="cf-steps">
        {steps.map((s) => (
          <div key={s.num} className="bg-[#FFFAF0] p-8 lg:p-10 group">
            <div className="flex items-center justify-between mb-8">
              <span className="text-[#B8965A] text-xs tracking-[0.3em]">{s.num}</span>
              <s.icon className="h-5 w-5 text-[#B8965A] transition-transform duration-300 group-hover:-translate-y-1" />
            </div>
            <h3 className="text-2xl font-light mb-4 text-[#1A1610]" style={{ fontFamily: "Cormorant Garamond, serif" }}>{s.title}</h3>
            <p className="text-sm text-[#6B5F4C] leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>

      <div className="mb-16">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#B8965A] mb-3">{t("cf_guarantees_eyebrow", lang)}</p>
        <h2 className="text-3xl sm:text-4xl font-light text-[#1A1610] mb-10" style={{ fontFamily: "Cormorant Garamond, serif" }}>
          {t("cf_guarantees_title", lang)}
        </h2>
        <div className="grid gap-4 md:grid-cols-3" data-testid="cf-guarantees">
          {guarantees.map((g) => (
            <div key={g.title} className="border border-[#D8CFBB] rounded-lg p-7 bg-[#FFFAF0]">
              <g.icon className="h-6 w-6 text-[#B8965A] mb-4" />
              <h4 className="text-xl font-light mb-3 text-[#1A1610]" style={{ fontFamily: "Cormorant Garamond, serif" }}>{g.title}</h4>
              <p className="text-sm text-[#6B5F4C] leading-relaxed">{g.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-[#B8965A]/30 bg-gradient-to-r from-[#B8965A]/5 via-transparent to-[#B8965A]/5 p-8 lg:p-12 rounded-lg mb-16" data-testid="cf-escrow">
        <div className="grid md:grid-cols-[auto_1fr] gap-6 items-center">
          <div className="h-14 w-14 rounded-full border border-[#B8965A] flex items-center justify-center">
            <Shield className="h-6 w-6 text-[#B8965A]" />
          </div>
          <div>
            <p className="text-xs tracking-[0.4em] uppercase text-[#B8965A] mb-2">{t("funds_held", lang)}</p>
            <h3 className="text-3xl font-light mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>{t("escrow_headline", lang)}</h3>
            <p className="text-sm text-[#4A3F30] leading-relaxed max-w-3xl">{t("escrow_notice", lang)}</p>
          </div>
        </div>
      </div>

      <div className="text-center">
        <button
          onClick={() => navigate(user ? "/dashboard" : "/login")}
          data-testid="cf-cta"
          className="bg-[#B8965A] text-white hover:bg-[#a08248] tracking-[0.25em] uppercase text-xs px-10 h-14 transition-colors duration-200"
        >
          {user ? t("network_cta_button_dashboard", lang) : t("cf_cta_new", lang)}
        </button>
      </div>
    </PageShell>
  );
}
